import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';
import { 
  LayoutDashboard, 
  FolderKanban, 
  DollarSign, 
  Package, 
  HardHat,
  Users,
  BarChart3,
  LogOut,
  Menu,
  X,
  FileText,
  Briefcase,
  Truck,
  MessageSquare,
  Folder,
  CheckCircle
} from 'lucide-react';

const ERPLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const navItems = [
    { path: '/erp/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/erp/projects', icon: FolderKanban, label: 'Projects' },
    { path: '/erp/tasks', icon: CheckCircle, label: 'Tasks' },
    { path: '/erp/boq', icon: FileText, label: 'BOQ' },
    { path: '/erp/finance', icon: DollarSign, label: 'Finance & Billing' },
    { path: '/erp/contracts', icon: Briefcase, label: 'Contracts' },
    { path: '/erp/procurement', icon: Package, label: 'Procurement' },
    { path: '/erp/resources', icon: Truck, label: 'Resources' },
    { path: '/erp/site-management', icon: HardHat, label: 'Site Management' },
    { path: '/erp/crm', icon: Users, label: 'CRM' },
    { path: '/erp/communication', icon: MessageSquare, label: 'Communication' },
    { path: '/erp/documents', icon: Folder, label: 'Documents' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-gradient-to-b from-[#214979] to-[#214979]/90 text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo & Close Button */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <Logo variant="icon" inverted className="h-10" />
              <div>
                <h1 className="text-xl font-bold">ERP System</h1>
                <p className="text-xs text-[#c0932a]/80">Construction Management</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:bg-white/10 p-2 rounded"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-lg
                      transition-all duration-200
                      ${isActive 
                        ? 'bg-white text-primary shadow-lg' 
                        : 'text-white hover:bg-white/10'
                      }
                    `}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/10">
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-white">{user?.name || user?.email}</p>
              <p className="text-xs text-white/60">{user?.role || 'Admin'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-4 py-2 rounded-lg
                       bg-white/10 hover:bg-white/20 transition-colors text-white"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Menu size={24} />
            </button>
            
            <div className="flex-1 lg:flex-none ml-3 lg:ml-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                Construction ERP
              </h2>
            </div>

            <div className="hidden sm:flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                ← Back to Website
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ERPLayout;